import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const C = {
  bg: '#0a0a0a', surface: '#141414', surface2: '#1a1a1a',
  border: '#222', text: '#fff', textMuted: '#777', textSub: '#aaa',
  accent: '#d4f535', green: '#4ade80', orange: '#fb923c',
  red: '#f87171', purple: '#a78bfa', blue: '#60a5fa',
};

const Icon = ({ name, size = 15, color = 'currentColor' }) => {
  const icons = {
    send: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>,
    folder: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    file: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    message: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
    link: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    chevronDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  };
  return icons[name] || null;
};

const fmt = (b) => !b ? '—' : b < 1048576 ? (parseInt(b)/1024).toFixed(0)+' Ko' : (parseInt(b)/1048576).toFixed(1)+' Mo';

const inputStyle = {
  width: '100%', padding: '12px 14px', borderRadius: 10,
  border: `1px solid #333`, fontSize: 13, outline: 'none',
  background: '#1a1a1a', color: '#fff', boxSizing: 'border-box',
};

export default function NotifyPage() {
  const [members, setMembers] = useState([]);
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sentResults, setSentResults] = useState(null);
  const [filterFolder, setFilterFolder] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [{ data: m }, { data: f }, { data: d }] = await Promise.all([
        api.get('/members'), api.get('/folders'), api.get('/documents')
      ]);
      setMembers(m); setFolders(f); setDocs(d);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const docsInFolder = filterFolder
    ? docs.filter(d => String(d.folder_id) === String(filterFolder))
    : docs;

  const filteredMembers = members.filter(m => {
    const q = search.toLowerCase();
    const matchSearch = !q || (m.name && m.name.toLowerCase().includes(q)) || (m.email && m.email.toLowerCase().includes(q));
    const matchFolder = !filterFolder || String(m.folder_id) === String(filterFolder);
    return matchSearch && matchFolder;
  });

  const toggleDoc = (id) => setSelectedDocs(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleMember = (id) => setSelectedMembers(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const selectAllMembers = () => setSelectedMembers(filteredMembers.map(m => m.id));
  const selectNoneMembers = () => setSelectedMembers([]);

  const handleSend = async () => {
    if (selectedMembers.length === 0) { toast.error('Sélectionnez au moins un membre'); return; }
    if (selectedDocs.length === 0) { toast.error('Sélectionnez au moins un document PDF'); return; }
    if (!window.confirm('Envoyer ' + selectedDocs.length + ' document(s) à ' + selectedMembers.length + ' membre(s) ?')) return;
    setSending(true); setSentResults(null);
    try {
      const { data } = await api.post('/notify/send-bulk', {
        memberIds: selectedMembers,
        customMessage: customMessage.trim(),
        documentIds: selectedDocs,
        folderId: filterFolder || null
      });
      setSentResults(data);
      if (data.failed.length === 0) toast.success(data.sent + ' email(s) envoyé(s) !');
      else toast.success(data.sent + ' envoyés, ' + data.failed.length + ' échecs');
      setSelectedMembers([]);
    } catch (err) { toast.error((err.response?.data?.error) || 'Erreur envoi'); }
    finally { setSending(false); }
  };

  const handleSendOne = async (member) => {
    if (selectedDocs.length === 0) { toast.error("Sélectionnez d'abord un document PDF"); return; }
    setSending(true);
    try {
      await api.post('/notify/send-link/' + member.id, {
        customMessage: customMessage.trim(),
        documentIds: selectedDocs,
        folderId: member.folder_id || null
      });
      toast.success('Email envoyé à ' + member.email);
    } catch (err) { toast.error((err.response?.data?.error) || 'Erreur'); }
    finally { setSending(false); }
  };

  const canSend = selectedMembers.length > 0 && selectedDocs.length > 0;

  return (
    <div style={{ padding: 16, background: '#0a0a0a', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 900, color: '#fff', margin: 0, letterSpacing: '-0.5px' }}>Envoyer des liens</h1>
        <p style={{ color: '#777', fontSize: 12, marginTop: 4 }}>Espace de contrôle documentaire</p>
        <div style={{ display:'flex', alignItems:'center', gap: 12, marginTop: 10 }}>
          <span style={{ display:'flex', alignItems:'center', gap: 5, fontSize: 10, color: '#d4f535' }}>
            <span style={{ width: 6, height: 6, borderRadius:'50%', background: '#d4f535', display:'inline-block' }}/>
            INSTANTANÉ · {new Date().toLocaleDateString('fr-FR',{day:'2-digit',month:'short',year:'numeric'}).toUpperCase()}
          </span>
        </div>
      </div>

      <div style={{ height: 1, background: '#222', marginBottom: 24 }}/>

      {/* ÉTAPE 01 — Dossier */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 14 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#d4f535', letterSpacing: 1 }}>01</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Dossier</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
        </div>
        <div style={{ position:'relative' }}>
          <select value={filterFolder} onChange={e => { setFilterFolder(e.target.value); setSelectedDocs([]); setSelectedMembers([]); }}
            style={{ ...inputStyle, paddingRight: 36, cursor:'pointer', appearance:'none', WebkitAppearance:'none' }}>
            <option value="">Tous les dossiers</option>
            {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <span style={{ position:'absolute', right: 12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </span>
        </div>
      </div>

      <div style={{ height: 1, background: '#222', marginBottom: 24 }}/>

      {/* ÉTAPE 02 — Documents PDF */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 14 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#d4f535', letterSpacing: 1 }}>02</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Documents PDF</span>
          </div>
          <span style={{ fontSize: 12, color: '#777' }}>{selectedDocs.length} / {docsInFolder.length}</span>
        </div>
        {docsInFolder.length === 0 ? (
          <div style={{ padding: 16, borderRadius: 10, background: '#141414', border:'1px solid #222', fontSize: 12, color: '#777', textAlign:'center' }}>
            Aucun document PDF {filterFolder ? 'dans ce dossier' : 'disponible'}
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
            {docsInFolder.map(d => {
              const isSel = selectedDocs.includes(d.id);
              return (
                <div key={d.id} onClick={() => toggleDoc(d.id)} style={{
                  display:'flex', alignItems:'center', gap: 12, padding: '14px 16px',
                  borderRadius: 10, border: `1px solid ${isSel ? '#d4f535' + '66' : '#222'}`,
                  background: isSel ? '#d4f535' + '0d' : '#141414', cursor:'pointer', transition:'all 0.15s'
                }}>
                  <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                    background: isSel ? '#d4f535' : 'transparent',
                    border: `2px solid ${isSel ? '#d4f535' : '#444'}`,
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {isSel && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                  </div>
                  <div style={{ width: 36, height: 36, borderRadius: 8, background: '#1a1a1a', border:'1px solid #222', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 2, flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isSel ? '#d4f535' : '#777'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span style={{ fontSize: 7, fontWeight: 800, color: isSel ? '#d4f535' : '#777', letterSpacing: 0.5 }}>PDF</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {d.original_name || d.title}
                    </div>
                    <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>
                      {fmt(d.size)}{d.total_pages ? ' · ' + d.total_pages + ' page' + (d.total_pages > 1 ? 's' : '') : ''}{d.folder_name ? ' · ' + d.folder_name : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ height: 1, background: '#222', marginBottom: 24 }}/>

      {/* ÉTAPE 03 — Destinataires */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 14 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#d4f535', letterSpacing: 1 }}>03</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Destinataires</span>
          </div>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </div>
        <div style={{ position:'relative', marginBottom: 10 }}>
          <span style={{ position:'absolute', left: 12, top:'50%', transform:'translateY(-50%)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un membre..."
            style={{ ...inputStyle, paddingLeft: 36 }}/>
        </div>
        <div style={{ display:'flex', gap: 8, marginBottom: 12, flexWrap:'wrap', alignItems:'center' }}>
          <button onClick={selectAllMembers} style={{ padding:'6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor:'pointer',
            background: '#d4f535' + '22', color: '#d4f535', border: '1px solid #d4f535' + '44' }}>
            Tout sélectionner ({filteredMembers.length})
          </button>
          <button onClick={selectNoneMembers} style={{ padding:'6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor:'pointer',
            background: '#1a1a1a', color: '#aaa', border: '1px solid #222' }}>
            Désélectionner
          </button>
          <span style={{ marginLeft:'auto', fontSize: 12, color: '#d4f535', fontWeight: 700 }}>
            {selectedMembers.length} sélectionné(s)
          </span>
        </div>
        <div style={{ background: '#141414', borderRadius: 10, border: '1px solid #222', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign:'center', color: '#777' }}>Chargement...</div>
          ) : filteredMembers.length === 0 ? (
            <div style={{ padding: 32, textAlign:'center', color: '#777' }}>
              Aucun membre{filterFolder ? ' dans ce dossier' : ''}
            </div>
          ) : filteredMembers.map((m, i) => {
            const isSel = selectedMembers.includes(m.id);
            const initials = m.name?.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) || '?';
            return (
              <div key={m.id} onClick={() => toggleMember(m.id)} style={{
                display:'flex', alignItems:'center', gap: 10, padding: '12px 16px',
                borderBottom: i < filteredMembers.length-1 ? '1px solid #222' : 'none',
                cursor:'pointer', background: isSel ? '#d4f535' + '0d' : 'transparent', transition:'background 0.1s'
              }}>
                <div style={{ width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  background: isSel ? '#d4f535' : 'transparent',
                  border: `2px solid ${isSel ? '#d4f535' : '#444'}`,
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {isSel && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: '#1a1a1a', border:'1px solid #222', display:'flex', alignItems:'center', justifyContent:'center', fontSize: 11, fontWeight: 800, color: '#d4f535', flexShrink: 0 }}>
                  {initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap: 6, flexWrap:'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{m.name}</span>
                    {m.folder_name && (
                      <span style={{ background: '#d4f535' + '18', color: '#d4f535', borderRadius: 4, padding:'1px 6px', fontSize: 9, fontWeight: 700 }}>{m.folder_name}</span>
                    )}
                    {m.version && <span style={{ background: '#1a1a1a', color: '#aaa', borderRadius: 4, padding:'1px 6px', fontSize: 9, fontWeight: 600, border:'1px solid #222' }}>v{m.version}</span>}
                    {m.total_opens > 0 && (
                      <span style={{ background: '#4ade80' + '18', color: '#4ade80', borderRadius: 4, padding:'1px 6px', fontSize: 9, fontWeight: 700 }}>Ouvert</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{m.email || m.member_email || 'Email non disponible'}</div>
                </div>
                <button onClick={e => { e.stopPropagation(); handleSendOne(m); }} disabled={sending}
                  style={{ padding:'7px 12px', borderRadius: 8, border:'1px solid #222', background: '#1a1a1a', color: '#aaa',
                    fontSize: 11, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', flexShrink: 0,
                    display:'flex', alignItems:'center', gap: 5 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Envoyer
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ height: 1, background: '#222', marginBottom: 24 }}/>

      {/* ÉTAPE 04 — Message */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 14 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 900, color: '#d4f535', letterSpacing: 1 }}>04</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#fff' }}>Message</span>
          </div>
          <span style={{ fontSize: 10, color: '#777', textTransform:'uppercase', letterSpacing: 1 }}>OPTIONNEL</span>
        </div>
        <textarea value={customMessage} onChange={e => setCustomMessage(e.target.value)}
          placeholder="Votre message aux destinataires..."
          rows={4} maxLength={1000}
          style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit', minHeight: 100 }}/>
        <div style={{ textAlign:'right', fontSize: 11, color: '#777', marginTop: 6 }}>
          {customMessage.length} / 1 000
        </div>
      </div>

      {/* Séparateur accent */}
      <div style={{ height: 2, background: '#d4f535', marginBottom: 24, borderRadius: 1 }}/>

      {/* RÉCAP VOTRE ENVOI */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: '#777', textTransform:'uppercase', letterSpacing: 1.5 }}>VOTRE ENVOI</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d4f535" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginBottom: 16 }}>Prêt à partager</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 1, background: '#222', borderRadius: 10, overflow:'hidden', marginBottom: 16 }}>
          <div style={{ background: '#141414', padding:'16px 14px' }}>
            <div style={{ fontSize: 11, color: '#777', marginBottom: 6 }}>Documents</div>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#d4f535', lineHeight: 1 }}>
              {String(selectedDocs.length).padStart(2,'0')}
            </div>
          </div>
          <div style={{ background: '#141414', padding:'16px 14px' }}>
            <div style={{ fontSize: 11, color: '#777', marginBottom: 6 }}>Destinataires</div>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
              {String(selectedMembers.length).padStart(2,'0')}
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: '#222', marginBottom: 14 }}/>
        <p style={{ fontSize: 12, color: '#777', marginBottom: 16 }}>
          {selectedDocs.length === 0 ? 'Aucun document sélectionné.' : selectedDocs.length + ' document(s) · ' + selectedMembers.length + ' destinataire(s)'}
        </p>

        {sentResults && (
          <div style={{ padding: 14, borderRadius: 10, marginBottom: 14,
            background: sentResults.failed.length > 0 ? '#2a1a00' : '#0a1f0f',
            border: `1px solid ${sentResults.failed.length > 0 ? '#fb923c44' : '#4ade8044'}`,
            fontSize: 13, color: sentResults.failed.length > 0 ? '#fb923c' : '#4ade80' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Résultats</div>
            <div>{sentResults.sent} email(s) envoyé(s) sur {sentResults.total}</div>
            {sentResults.failed.map((f, i) => (
              <div key={i} style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>{f.email} — {f.error}</div>
            ))}
          </div>
        )}

        <button onClick={handleSend} disabled={sending || !canSend}
          style={{ width:'100%', padding: 14, borderRadius: 12, border:'none',
            background: canSend ? '#d4f535' : '#1a1a1a',
            color: canSend ? '#0a0a0a' : '#777',
            fontWeight: 700, fontSize: 14, cursor: canSend ? 'pointer' : 'not-allowed',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 8,
            transition:'all 0.15s' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={canSend ? '#0a0a0a' : '#777'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
          {sending ? 'Envoi en cours...'
            : selectedDocs.length === 0 ? "Sélectionnez d'abord un document PDF"
            : selectedMembers.length === 0 ? 'Sélectionnez des membres'
            : "Prévisualiser l'envoi →"}
        </button>

        <div style={{ display:'flex', alignItems:'center', gap: 6, marginTop: 10 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <span style={{ fontSize: 10, color: '#777' }}>Mode aperçu · aucun e-mail envoyé.</span>
        </div>
      </div>
    </div>
  );
}
